import { inject, injectable } from 'inversify';
import { serializeError } from 'serialize-error';
import { FIFTEEN_SECONDS_IN_MS } from '../../constants/common';
import { TOKEN } from '../../inversify.tokens';
import type { Logger } from '../../types/common';
import type { LoggerFactory } from '../../types/container';
import SignalingApiClient from '../signalingApiClient/SignalingApiClient';

@injectable()
class SignalingHeartbeater {
  #heartbeatIntervalId?: ReturnType<typeof setInterval>;

  readonly #logger: Logger;

  constructor(
    @inject(TOKEN.LoggerFactory) loggerFactory: LoggerFactory,
    @inject(TOKEN.SignalingApiClient) private readonly signalingApiClient: SignalingApiClient,
  ) {
    this.#logger = loggerFactory('SignalingHeartbeater');
  }

  start(): void {
    if (this.#heartbeatIntervalId) {
      return;
    }

    this.#heartbeatIntervalId = setInterval(async () => {
      await this.sendHeartbeat();
    }, FIFTEEN_SECONDS_IN_MS);

    this.#logger.debug('Signaling heartbeat interval started');
  }

  dispose(): void {
    if (!this.#heartbeatIntervalId) {
      return;
    }

    clearInterval(this.#heartbeatIntervalId);
    this.#heartbeatIntervalId = undefined;
    this.#logger.debug('Signaling heartbeat interval stopped');
  }

  private async sendHeartbeat(): Promise<void> {
    try {
      await this.signalingApiClient.ping.pingPeer();
    } catch (error) {
      this.#logger.debug('Failed to send signaling heartbeat', {
        error: serializeError(error),
      });
    }
  }
}

export default SignalingHeartbeater;
